import { useMemo, type FC } from "react";
import { SidebarGroup, SidebarGroupContent, SidebarMenu, SidebarMenuItem, SidebarMenuButton } from "@capacit-com/lara-law-shared-ui";
import {  } from "@capacit-com/lara-law-shared-ui";
import { ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDocumentReferenceStore } from "@/stores/document-reference.store";
import DocumentReference from "@/models/conversation/document";
import {
  showErrorToast,
  showInfoToast,
} from "@/components/custom-toasts/toasts";
import { cn } from "@/lib/utils";
import { useWorkspaceDocumentVersionStore } from "@/stores/workspace-document-version.store";
import { navigateToReference } from "@/utils/referenceNavigation";
import { NEW_TAB_MODIFIER_TOOLTIP } from "@/lib/navigation/appNavigation";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,  } from "@capacit-com/lara-law-shared-ui";
import { useConversationHub } from "@/hooks/useConversationHub";

const ChatReferencesTab: FC = () => {
  const navigate = useNavigate();
  const {
    documentReferences: references,
    setSelectedBoundingBoxes,
    setSelectedDocumentReference,
    setDocumentReferences,
    selectedDocumentReference,
    documentRefIdsOrdered,
  } = useDocumentReferenceStore();
  const { resetWorkspaceDocumentVersionStore } =
    useWorkspaceDocumentVersionStore();
  const { selectedConversation } = useConversationHub();

  const handleSourceClick = (
    reference: DocumentReference,
    event: React.MouseEvent
  ) => {
    navigateToReference({
      reference,
      references,
      navigate,
      event,
      store: {
        setSelectedBoundingBoxes,
        setSelectedDocumentReference,
        setDocumentReferences,
        resetWorkspaceDocumentVersionStore,
      },
      onError: (message) => showErrorToast(message),
      onDocumentIndexTypeDocument: () =>
        showInfoToast(
          "Document index type burde ikke findes i Web appen. Hvis du ser dette, så kontakt support."
        ),
      conversationId: selectedConversation?.id,
    });
  };

  const sortedReferences = useMemo(() => {
    return [...references].sort((a, b) => {
      const indexA = documentRefIdsOrdered.indexOf(a.referenceId.toString());
      const indexB = documentRefIdsOrdered.indexOf(b.referenceId.toString());

      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });
  }, [references, documentRefIdsOrdered]);

  return (
    <>
      {sortedReferences.length === 0 ? (
        <div className="flex items-center justify-center w-full h-full p-4">
          <p className="text-sm text-sidebar-foreground w-[80%] text-center">
            Ingen kildehenvisninger tilgængelige
          </p>
        </div>
      ) : (
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {sortedReferences.map((source) => (
                <SidebarMenuItem key={source.chunkId}>
                  <TooltipProvider delayDuration={500}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <SidebarMenuButton
                          asChild
                          className={cn(
                            "cursor-pointer",
                            selectedDocumentReference?.chunkId ===
                              source.chunkId
                              ? "bg-accent text-accent-foreground"
                              : "text-sidebar-foreground"
                          )}
                        >
                          <div
                            rel="noopener noreferrer"
                            className="flex justify-between w-full items-center"
                            onClick={(e) => handleSourceClick(source, e)}
                          >
                            <div className="flex flex-col w-0 flex-1">
                              <span className="font-medium text-xs truncate">
                                {source.documentTitle}
                              </span>
                            </div>
                            <ExternalLink className="size-4 text-sidebar-foreground shrink-0" />
                          </div>
                        </SidebarMenuButton>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p>{NEW_TAB_MODIFIER_TOOLTIP}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}
    </>
  );
};

export default ChatReferencesTab;
